/**
 * @fileoverview Event Form Dialog — Professional Multi-Step
 * @description A polished stepper dialog for creating/editing calendar events,
 *              matching the Alaska Airlines / GitHub Metrics dashboard aesthetic.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, MenuItem, FormControlLabel, Switch,
  IconButton, Typography, Box, Alert, Stepper, Step,
  StepLabel, Avatar, Divider, Fade, InputAdornment,
} from '@mui/material';
import {
  X, CalendarDays, Layers, Link2, ChevronRight, ChevronLeft,
  CheckCircle2, AlertCircle, Tag, Rocket,
  Users, GitBranch, ExternalLink, BarChart3, Save,
} from 'lucide-react';
import { PRIORITY_OPTIONS, RECURRENCE_OPTIONS } from '../../constants';

/* ─── palette (matches Dashboard.css) ─── */
const P = {
  navy: '#01426A', navyDark: '#002F5F',
  teal: '#0097A9', tealLight: '#E6F7F9',
  white: '#FFFFFF', bg: '#F8FAFC',
  border: '#E2E8F0', text: '#1E293B',
  sub: '#64748B', muted: '#94A3B8',
  ok: '#00AF66', err: '#E31837',
};

const STEPS = [
  { label: 'Basics', icon: CalendarDays },
  { label: 'Details', icon: Layers },
  { label: 'Metrics', icon: BarChart3 },
  { label: 'Links', icon: Link2 },
];

const BLANK = {
  title: '', description: '', start_date: '', end_date: '',
  is_all_day: false, is_recurring: false, recurrence_pattern: '',
  test_type_id: '', environment_id: '', team_id: '',
  color_code: '#4A90D9', priority: 'MEDIUM', status: 'PLANNED',
  application_name: '', module_name: '', sprint_name: '', release_version: '',
  total_test_cases: 0, automated_test_cases: 0, manual_test_cases: 0,
  jira_ticket: '', ado_work_item_id: '', github_repo: '',
  test_plan_url: '', pipeline_url: '', notes: '', tags: '',
};

/* ─── compact field style ─── */
const fx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px', fontSize: '0.875rem', backgroundColor: P.white,
    transition: 'all .2s',
    '& fieldset': { borderColor: P.border },
    '&:hover fieldset': { borderColor: P.teal },
    '&.Mui-focused fieldset': { borderColor: P.teal, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': { fontSize: '0.8rem', color: P.sub, '&.Mui-focused': { color: P.teal } },
  '& .MuiOutlinedInput-input': { padding: '10px 12px' },
  '& .MuiSelect-select': { padding: '10px 12px' },
};

const secTitle = { display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5, '& svg': { color: P.teal } };

/* custom step icon */
const SIcon = ({ icon: Icon, active, completed }) => (
  <Avatar sx={{
    width: 32, height: 32, transition: 'all .3s',
    bgcolor: completed ? P.ok : active ? P.teal : P.border,
  }}>
    {completed
      ? <CheckCircle2 size={16} color={P.white} />
      : <Icon size={16} color={active ? P.white : P.muted} />}
  </Avatar>
);

/* ═══════════════════════════════════════ */

const EventFormDialog = ({
  open, onClose, onSubmit,
  event = null, teams = [], environments = [], testTypes = [],
}) => {
  const isEdit = Boolean(event?.id);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    setStep(0); setTouched({}); setError('');
    if (event) {
      setForm({
        ...BLANK, ...event,
        start_date: event.start_date?.slice(0, 16) || '',
        end_date: event.end_date?.slice(0, 16) || '',
        test_type_id: event.test_type_id ?? '',
        environment_id: event.environment_id ?? '',
        team_id: event.team_id ?? '',
        color_code: event.color_code || '#4A90D9',
        tags: event.tags || '',
      });
    } else {
      setForm(BLANK);
    }
  }, [event, open]);

  const set = (f) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [f]: v }));
    setTouched((p) => ({ ...p, [f]: true }));
  };
  const tog = (f) => () => setForm((p) => ({ ...p, [f]: !p[f] }));

  /* validation */
  const errs = useMemo(() => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.start_date) e.start_date = 'Required';
    if (!form.end_date) e.end_date = 'Required';
    if (form.start_date && form.end_date && new Date(form.end_date) < new Date(form.start_date))
      e.end_date = 'Must be after start';
    return e;
  }, [form.title, form.start_date, form.end_date]);

  const s0ok = !errs.title && !errs.start_date && !errs.end_date;
  const canGo = (s) => s === 0 ? s0ok : true;

  const next = () => {
    if (step === 0 && !s0ok) { setTouched({ title: true, start_date: true, end_date: true }); return; }
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!s0ok) { setStep(0); setTouched({ title: true, start_date: true, end_date: true }); return; }
    setSubmitting(true); setError('');
    try {
      const d = { ...form };
      ['test_type_id', 'environment_id', 'team_id'].forEach((k) => {
        d[k] = d[k] === '' ? null : Number(d[k]);
      });
      if (!d.color_code) delete d.color_code;
      if (!d.recurrence_pattern) { d.is_recurring = false; d.recurrence_pattern = null; }
      await onSubmit(d);
      onClose();
    } catch (e) { setError(e.message || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  const fe = (n) => touched[n] && errs[n] ? { error: true, helperText: errs[n] } : {};
  const sT = Array.isArray(teams) ? teams : [];
  const sTT = Array.isArray(testTypes) ? testTypes : [];
  const sE = Array.isArray(environments) ? environments : [];

  /* ── STEP 0: Basics ── */
  const Step0 = () => (
    <Fade in timeout={300}><Box>
      <TextField label="Event Title" value={form.title} onChange={set('title')} fullWidth required autoFocus
        placeholder="e.g. Sprint 12 — Regression Test" sx={{ ...fx, mb: 2 }} {...fe('title')} />
      <TextField label="Description" value={form.description} onChange={set('description')} fullWidth
        multiline rows={2} placeholder="Brief summary…" sx={{ ...fx, mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <TextField label="Start" type="datetime-local" value={form.start_date} onChange={set('start_date')}
          fullWidth required slotProps={{ inputLabel: { shrink: true } }} sx={fx} {...fe('start_date')} />
        <TextField label="End" type="datetime-local" value={form.end_date} onChange={set('end_date')}
          fullWidth required slotProps={{ inputLabel: { shrink: true } }} sx={fx} {...fe('end_date')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
        <FormControlLabel control={
          <Switch checked={form.is_all_day} onChange={tog('is_all_day')} size="small"
            sx={{ '& .Mui-checked': { color: P.teal }, '& .Mui-checked+.MuiSwitch-track': { bgcolor: P.teal } }} />
        } label={<Typography variant="body2" sx={{ fontSize: '0.8rem', color: P.sub }}>All Day</Typography>} />
        <FormControlLabel control={
          <Switch checked={form.is_recurring} onChange={tog('is_recurring')} size="small"
            sx={{ '& .Mui-checked': { color: P.teal }, '& .Mui-checked+.MuiSwitch-track': { bgcolor: P.teal } }} />
        } label={<Typography variant="body2" sx={{ fontSize: '0.8rem', color: P.sub }}>Recurring</Typography>} />
      </Box>
      {form.is_recurring && (
        <Fade in><TextField select label="Repeat" value={form.recurrence_pattern || ''} onChange={set('recurrence_pattern')}
          fullWidth sx={{ ...fx, mb: 1.5 }}>
          {RECURRENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField></Fade>
      )}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField select label="Priority" value={form.priority} onChange={set('priority')} fullWidth sx={fx}>
          {PRIORITY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: o.color }} />
                <span>{o.label}</span>
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField label="Color" type="color" value={form.color_code || '#4A90D9'} onChange={set('color_code')}
          sx={{ ...fx, width: 80, flexShrink: 0, '& .MuiOutlinedInput-input': { p: '6px', height: 28, cursor: 'pointer' } }}
          slotProps={{ inputLabel: { shrink: true } }} />
      </Box>
    </Box></Fade>
  );

  /* ── STEP 1: Details ── */
  const Step1 = () => (
    <Fade in timeout={300}><Box>
      <Typography variant="subtitle2" sx={secTitle}><Users size={16} /> Team & Classification</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField select label="Team" value={form.team_id} onChange={set('team_id')} fullWidth sx={fx}>
          <MenuItem value="">— None —</MenuItem>
          {sT.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color_code || '#4A90D9' }} />
                {t.team_name}
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Test Type" value={form.test_type_id} onChange={set('test_type_id')} fullWidth sx={fx}>
          <MenuItem value="">— None —</MenuItem>
          {sTT.map((t) => <MenuItem key={t.id} value={t.id}>{t.type_name}</MenuItem>)}
        </TextField>
        <TextField select label="Environment" value={form.environment_id} onChange={set('environment_id')} fullWidth sx={fx}>
          <MenuItem value="">— None —</MenuItem>
          {sE.map((e) => <MenuItem key={e.id} value={e.id}>{e.environment_name}</MenuItem>)}
        </TextField>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={secTitle}><Rocket size={16} /> Project Context</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        <TextField label="Application" value={form.application_name} onChange={set('application_name')} sx={fx} placeholder="My App" />
        <TextField label="Module / Feature" value={form.module_name} onChange={set('module_name')} sx={fx} placeholder="Payments" />
        <TextField label="Sprint" value={form.sprint_name} onChange={set('sprint_name')} sx={fx} placeholder="Sprint 12" />
        <TextField label="Release Version" value={form.release_version} onChange={set('release_version')} sx={fx} placeholder="v2.4.0" />
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={secTitle}><Tag size={16} /> Tags & Notes</Typography>
      <TextField label="Tags" value={form.tags} onChange={set('tags')} fullWidth
        placeholder="api, smoke, regression (comma-separated)" sx={{ ...fx, mb: 1.5 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Tag size={14} color={P.muted} /></InputAdornment> } }} />
      <TextField label="Notes" value={form.notes} onChange={set('notes')} fullWidth multiline rows={2} sx={fx} placeholder="Additional context…" />
    </Box></Fade>
  );

  /* ── STEP 2: Metrics ── */
  const Step2 = () => {
    const tot = Number(form.total_test_cases) || 0;
    const aut = Number(form.automated_test_cases) || 0;
    const pct = tot > 0 ? Math.round((aut / tot) * 100) : 0;
    return (
      <Fade in timeout={300}><Box>
        <Typography variant="subtitle2" sx={secTitle}><BarChart3 size={16} /> Planned Test Metrics</Typography>
        {tot > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5, borderRadius: '8px', bgcolor: P.tealLight, border: `1px solid ${P.border}` }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}>
                <Typography variant="caption" sx={{ color: P.sub, fontWeight: 600 }}>Automation Coverage</Typography>
                <Typography variant="caption" sx={{ color: P.teal, fontWeight: 700 }}>{pct}%</Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#E2E8F0', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: `linear-gradient(90deg, ${P.teal}, ${P.navy})`, transition: 'width .4s' }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: P.navy, lineHeight: 1 }}>{tot}</Typography>
              <Typography variant="caption" sx={{ color: P.muted, fontSize: '0.65rem' }}>TOTAL</Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          <TextField label="Total Cases" type="number" value={form.total_test_cases} onChange={set('total_test_cases')} sx={fx} slotProps={{ htmlInput: { min: 0 } }} />
          <TextField label="Automated" type="number" value={form.automated_test_cases} onChange={set('automated_test_cases')} sx={fx} slotProps={{ htmlInput: { min: 0 } }} />
          <TextField label="Manual" type="number" value={form.manual_test_cases} onChange={set('manual_test_cases')} sx={fx} slotProps={{ htmlInput: { min: 0 } }} />
        </Box>
      </Box></Fade>
    );
  };

  /* ── STEP 3: Links ── */
  const Step3 = () => (
    <Fade in timeout={300}><Box>
      <Typography variant="subtitle2" sx={secTitle}><Link2 size={16} /> External References</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <TextField label="JIRA Ticket" value={form.jira_ticket} onChange={set('jira_ticket')} sx={fx} placeholder="PROJ-1234"
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><ExternalLink size={14} color={P.muted} /></InputAdornment> } }} />
        <TextField label="ADO Work Item" value={form.ado_work_item_id} onChange={set('ado_work_item_id')} sx={fx} placeholder="12345" />
        <TextField label="GitHub Repo" value={form.github_repo} onChange={set('github_repo')} sx={fx} placeholder="org/repo-name"
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><GitBranch size={14} color={P.muted} /></InputAdornment> } }} />
        <TextField label="Pipeline URL" value={form.pipeline_url} onChange={set('pipeline_url')} sx={fx} placeholder="https://…" />
        <TextField label="Test Plan URL" value={form.test_plan_url} onChange={set('test_plan_url')}
          sx={{ ...fx, gridColumn: '1 / -1' }} placeholder="https://…" />
      </Box>
    </Box></Fade>
  );

  const panels = [Step0, Step1, Step2, Step3];

  /* ═══════════════════ RENDER ═══════════════════ */
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,.25)', maxHeight: '90vh' } }}>

      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${P.navy} 0%, ${P.teal} 100%)`, px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ color: P.white, fontWeight: 700, fontSize: '1.1rem' }}>
            {isEdit ? 'Edit Event' : 'New Test Event'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.7)' }}>
            {STEPS[step].label} — Step {step + 1} of {STEPS.length}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,.8)', '&:hover': { color: P.white } }}>
          <X size={20} />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2, pb: 1, bgcolor: P.bg }}>
        <Stepper activeStep={step} alternativeLabel sx={{
          '& .MuiStepConnector-line': { borderColor: P.border },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: P.teal },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: P.ok },
        }}>
          {STEPS.map((s, i) => (
            <Step key={s.label} completed={i < step}
              onClick={() => (i < step || canGo(step)) && setStep(i)} sx={{ cursor: 'pointer' }}>
              <StepLabel StepIconComponent={() => <SIcon icon={s.icon} active={i === step} completed={i < step} />}>
                <Typography variant="caption"
                  sx={{ fontWeight: i === step ? 600 : 400, color: i === step ? P.navy : P.muted, fontSize: '0.7rem' }}>
                  {s.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2, bgcolor: P.bg, minHeight: 280 }}>
        {error && <Alert severity="error" variant="filled" sx={{ mb: 2, borderRadius: '8px', fontSize: '0.8rem' }} icon={<AlertCircle size={16} />}>{error}</Alert>}
        {panels[step]()}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: P.white, borderTop: `1px solid ${P.border}`, justifyContent: 'space-between' }}>
        <Box>
          {step > 0 && (
            <Button onClick={back} startIcon={<ChevronLeft size={16} />}
              sx={{ color: P.sub, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}>
              Back
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}
            sx={{ color: P.sub, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}>
            Cancel
          </Button>
          {step < 3 && (
            <Button variant="outlined" onClick={next} endIcon={<ChevronRight size={16} />}
              sx={{ borderColor: P.border, color: P.navy, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', borderRadius: '8px', px: 2, '&:hover': { borderColor: P.teal, color: P.teal } }}>
              Next
            </Button>
          )}
          <Button variant="contained" onClick={submit} disabled={submitting}
            startIcon={submitting ? null : <Save size={16} />}
            sx={{ background: `linear-gradient(135deg, ${P.navy}, ${P.teal})`, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', px: 2.5, '&:hover': { background: `linear-gradient(135deg, ${P.navyDark}, ${P.navy})` } }}>
            {submitting ? 'Saving…' : isEdit ? 'Update Event' : 'Create Event'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { EventFormDialog };
export default EventFormDialog;
